import { Link, type MetaFunction, useLoaderData } from "react-router";

import type { WritingCategory } from "./categories";
import { WritingPostList } from "./post-list";
import type { WritingPostSummary } from "./post-schema";

export interface WritingCategoryLoaderResponse {
  category: WritingCategory;
  posts: WritingPostSummary[];
}

export const meta: MetaFunction = ({ data }) => {
  const loaderData = data as WritingCategoryLoaderResponse | undefined;

  if (!loaderData) {
    return [{ title: `Writing | ${publicEnv.siteTitle}` }];
  }

  const description = loaderData.category.description;
  const title = `${loaderData.category.label} writing | ${publicEnv.siteTitle}`;
  const url =
    `https://${publicEnv.siteDomain}/writing/categories/` +
    loaderData.category.slug;

  return [
    { title },
    { tagName: "link", rel: "canonical", href: url },
    { name: "description", content: description },
    { property: "og:description", content: description },
    { property: "og:title", content: title },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
  ];
};

// Show one category without introducing a second taxonomy such as tags.
export default function WritingCategoryPage() {
  const { category, posts } = useLoaderData<WritingCategoryLoaderResponse>();

  return (
    <main
      className="mx-auto w-[calc(100%-2rem)] max-w-4xl pb-20 sm:w-[calc(100%-3rem)] sm:pb-28"
      id="main-content"
    >
      <header className="max-w-3xl pt-12 pb-16 sm:pt-20 sm:pb-24">
        <Link
          className="text-muted-foreground hover:text-primary text-sm font-semibold"
          to="/writing"
        >
          ← All writing
        </Link>
        <h1 className="mt-8 font-serif text-5xl font-semibold tracking-[-0.04em] sm:text-6xl">
          {category.label}
        </h1>
        <p className="text-muted-foreground mt-7 max-w-2xl text-xl leading-9">
          {category.description}
        </p>
      </header>

      <section className="border-t pt-12 sm:pt-16">
        <h2 className="font-serif text-3xl font-semibold tracking-[-0.025em]">
          {category.label} writing
        </h2>
        <WritingPostList posts={posts} />
      </section>
    </main>
  );
}
